<template>
  <div class="d-flex flex-column ga-3">
    <div class="d-flex align-center justify-space-between flex-wrap ga-2">
      <div>
        <div class="text-body-2 font-weight-semibold ncrm-text-strong">
          {{ $t("ncrm.workflows.nodeSettings.externalForms.notificationsTitle") }}
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ $t("ncrm.workflows.nodeSettings.externalForms.notificationsHint") }}
        </div>
      </div>
      <v-switch
        v-model="form.notifications.email.enabled"
        color="primary"
        hide-details
        :label="$t('ncrm.workflows.nodeSettings.externalForms.emailNotificationEnabled')"
      />
    </div>

    <v-row dense>
      <v-col cols="12" md="6">
        <v-textarea
          v-model="emailToText"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.emailRecipients')"
          rows="2"
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-textarea
          v-model="emailCcText"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.emailCc')"
          rows="2"
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12">
        <v-text-field
          v-model="form.notifications.email.subject"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.emailSubject')"
          hide-details="auto"
        />
      </v-col>
    </v-row>

    <!-- Sprint 6a — Telegram notification (mirrors email content) -->
    <v-divider class="my-2" />
    <div class="d-flex align-center justify-space-between flex-wrap ga-2">
      <div>
        <div class="text-body-2 font-weight-semibold ncrm-text-strong">
          {{ $t("ncrm.workflows.nodeSettings.externalForms.telegramTitle") }}
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ $t("ncrm.workflows.nodeSettings.externalForms.telegramHint") }}
        </div>
      </div>
      <v-switch
        v-model="telegramEnabled"
        color="primary"
        hide-details
        :label="$t('ncrm.workflows.nodeSettings.externalForms.telegramEnabled')"
      />
    </div>

    <template v-if="telegramEnabled">
      <v-alert
        v-if="!loadingRecipients && recipientOptions.length === 0"
        type="info"
        density="compact"
      >
        {{ $t("ncrm.workflows.nodeSettings.externalForms.telegramNoRecipients") }}
      </v-alert>
      <v-select
        v-else
        v-model="selectedUserIds"
        :items="recipientOptions"
        :label="$t('ncrm.workflows.nodeSettings.externalForms.telegramRecipients')"
        :loading="loadingRecipients"
        multiple
        chips
        closable-chips
        hide-details="auto"
        :menu-props="telegramMenuProps"
      />
    </template>
  </div>
</template>

<script>
import axios from "axios";

const parseEmailListText = (value) =>
  String(value || "").split(/[\n,]+/).map((email) => email.trim().toLowerCase()).filter(Boolean);
const formatEmailListText = (emails = []) => (Array.isArray(emails) ? emails : []).join("\n");

export default {
  name: "NcrmWorkflowExternalFormNotifications",
  props: {
    form: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loadingRecipients: false,
      team: [],
      // Render the menu above the settings drawer (z-index ~2700); default
      // Vuetify menu z-index (2000) would hide options behind the drawer.
      telegramMenuProps: { maxHeight: 320, zIndex: 2710 },
    };
  },
  computed: {
    emailToText: {
      get() {
        return formatEmailListText(this.form.notifications?.email?.to);
      },
      set(value) {
        this.form.notifications.email.to = parseEmailListText(value);
      },
    },
    emailCcText: {
      get() {
        return formatEmailListText(this.form.notifications?.email?.cc);
      },
      set(value) {
        this.form.notifications.email.cc = parseEmailListText(value);
      },
    },
    telegramEnabled: {
      get() {
        return !!this.form.notifications?.telegram?.enabled;
      },
      set(value) {
        this.ensureTelegramConfig();
        this.form.notifications.telegram.enabled = value;
        if (value && this.team.length === 0) this.fetchRecipients();
      },
    },
    selectedUserIds: {
      get() {
        const ids = this.form.notifications?.telegram?.recipientUserIds;
        return Array.isArray(ids) ? ids : [];
      },
      set(value) {
        this.ensureTelegramConfig();
        this.form.notifications.telegram.recipientUserIds = Array.isArray(value)
          ? value.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
          : [];
      },
    },
    recipientOptions() {
      return this.team.map((r) => {
        const name = r.user_name || r.user_email || `User #${r.user_id}`;
        const tg = r.telegram_username ? `@${r.telegram_username}` : `chat ${r.telegram_chat_id}`;
        return { title: `${name} (${tg})`, value: Number(r.user_id) };
      });
    },
  },
  mounted() {
    this.ensureTelegramConfig();
    // Pre-fetch on open so the linked-teammates list is ready instantly when
    // the user enables Telegram (avoids the empty-during-load window).
    this.fetchRecipients();
  },
  methods: {
    ensureTelegramConfig() {
      if (!this.form.notifications) this.form.notifications = {};
      if (!this.form.notifications.telegram) {
        this.form.notifications.telegram = { enabled: false, recipientUserIds: [] };
      }
      if (!Array.isArray(this.form.notifications.telegram.recipientUserIds)) {
        this.form.notifications.telegram.recipientUserIds = [];
      }
    },
    async fetchRecipients() {
      this.loadingRecipients = true;
      try {
        const { data: accountsData } = await axios.get("/integrations/telegram/link/accounts");
        const accounts = Array.isArray(accountsData?.accounts) ? accountsData.accounts : [];
        if (!accounts.length) {
          this.team = [];
          return;
        }
        const accountId = accounts[0].id;
        const { data } = await axios.get(
          `/integrations/telegram/accounts/${accountId}/recipients`
        );
        this.team = Array.isArray(data?.team) ? data.team : [];
      } catch (_) {
        this.team = [];
      } finally {
        this.loadingRecipients = false;
      }
    },
  },
};
</script>
